import { ActiveTab } from "../Table/constants";

// Must match the `gap-1` / `p-1` on the strip below — the overflow math
// reads element widths but has no way to read the flex gap.
const TAB_GAP = 4;
export const STRIP_PADDING = 8;

export interface OverflowLayout {
  visible: ActiveTab[];
  overflow: ActiveTab[];
}

// Priority-plus: tabs are shown in order until the row is full; the rest go
// under "More". The active tab is always kept visible, even when it is not
// among the first ones that fit, so the selected state is never hidden in a
// menu.
export const computeOverflowLayout = ({
  values,
  widths,
  activeValue,
  moreWidth,
  availableWidth
}: {
  values: ActiveTab[];
  widths: number[];
  activeValue: ActiveTab;
  moreWidth: number;
  availableWidth: number;
}): OverflowLayout => {
  const totalWidth =
    widths.reduce((sum, width) => sum + width, 0) +
    TAB_GAP * Math.max(values.length - 1, 0);
  if (totalWidth <= availableWidth) return { visible: values, overflow: [] };

  const included = new Set<ActiveTab>();
  let remaining = availableWidth - moreWidth;

  const activeIndex = values.indexOf(activeValue);
  if (activeIndex >= 0) {
    included.add(activeValue);
    remaining -= widths[activeIndex] + TAB_GAP;
  }

  for (let index = 0; index < values.length; index++) {
    if (index === activeIndex) continue;
    const needed = widths[index] + TAB_GAP;
    if (needed > remaining) break;
    included.add(values[index]);
    remaining -= needed;
  }

  return {
    visible: values.filter((value) => included.has(value)),
    overflow: values.filter((value) => !included.has(value))
  };
};
